import { BuildSteps, DeploySteps, Step } from "@rapid/types/flows";

export enum AnalyticsFlowEvent {
    // Page Views
    PAGE_VIEW = "Page View",
    BACK = "Back",
    // Home Page
    FEEDBACK_OPENED = "Feedback Opened",
    FEEDBACK_SUBMITTED = "Feedback Submitted",
    PROJECT_IDEA_SUBMITTED = "Project Idea Submitted",
    TASK_SELECTED = "Task Selected",
    DISABLED_TASK_CLICKED = "Disabled Task Clicked",
    // Upload
    FILE_UPLOADER_CLICKED = "File Uploader Clicked",
    FILE_SELECT_CLICKED = "File Select Clicked",
    FILE_INPUT_CLICKED = "File Input Clicked",
    CAMERA_INPUT_CLICKED = "Camera Input Clicked",
    QR_UPLOAD_BUTTON_CLICKED = "QR Upload Button Clicked",
    EXAMPLE_VIDEO_CLICKED = "Example Video Clicked",
    EXAMPLE_VIDEO_USED = "Example Video Used",
    TRIM_STARTED = "Trim Started",
    TRIM_CANCELED = "Trim Canceled",
    UPLOAD_CANCELED = "Upload Canceled",
    FILE_UPLOADED = "File Uploaded",
    UPLOAD_LIMIT_ERROR = "Upload Limit Error",
    FIND_MY_OBJECTS_CLICKED = "Find My Objects Clicked",
    EXTRACTED_OBJECTS_FROM_PROMPT = "Extracted Objects From Prompt",
    // Prompt
    BUILD_MODEL_CLICKED = "Build Model Clicked",
    ANNOTATION_BOX_DRAWN = "Annotation Box Drawn",
    GENERATE_PREDICTIONS_BUTTON_CLICKED = "Prompt Box Submitted",
    APPROVE_BUTTON_CLICKED = "Annotation Approve Clicked",
    REJECT_BUTTON_CLICKED = "Annotation Reject Clicked",
    FILE_NAVIGATION_CLICKED = "File Navigation Clicked",
    ANNOTATION_OBJECT_DELETED = "Annotation Box Deleted",
    PREDICTION_OBJECT_DELETED = "Prediction Box Deleted",
    ANNOTATION_FIND_SIMILAR = "Find Similar Objects Clicked",
    SMART_CHIP_CLICKED = "Prompt Suggestion Clicked",
    NEW_OBJECT_ADDED = "Annotation Class Drafted",
    EXCESSIVE_LABELLING_HINT_SHOWN = "Excessive Labelling Hint Shown",
    BACKSPACE_DELETE_HINT_SHOWN = "Backspace Delete Hint Shown",
    TOGGLE_TEXT_PROMPT_MODE = "Text Mode Toggle Clicked",
    TOGGLE_TEXT_PROMPT_MODE_MODAL_OPENED = "Text Mode Modal Opened",
    // Review / Preview
    IMPROVE_MODEL_CLICKED = "Improve Model Clicked",
    IMPROVE_MODAL_CLOSED = "Improve Modal Closed",
    IMPROVE_IMAGE_APPROVED = "Improve Image Approved",
    REPROMPT_CLICKED = "Reprompt Clicked",
    TEST_MODEL_CLICKED = "Test Model Clicked",
    MODEL_TESTED = "Model Tested",
    DOWNLOAD_ANNOTATED_CLICKED = "Download Annotated Clicked",
    DOWNLOADED_ANNOTATED_FILE = "Downloaded Annotated File",
    GET_API_KEY_CLICKED = "Get API Key Clicked",
    CREATE_WORKFLOW_STARTED = "Create Workflow Started",
    WORKFLOW_TEMPLATE_CLICKED = "Workflow Template Selected",
    WORKFLOW_CREATED = "Workflow Created",

    // Deploy
    DATA_MODE_SELECTED = "Data Mode Selected",
    CODE_SNIPPET_COPIED = "Code Snippet Copied",
    MARKDOWN_COPY_CLICKED = "Markdown Copy Clicked",
    DOCS_BUTTON_CLICKED = "Docs Button Clicked",
    CHATGPT_BUTTON_CLICKED = "ChatGPT Button Clicked",
    CLAUDE_BUTTON_CLICKED = "Claude Button Clicked",
    LOVABLE_BUTTON_CLICKED = "Lovable Button Clicked",
    OPEN_WORKFLOWS_BUTTON_CLICKED = "Open Workflows Button Clicked",
    LOCAL_DEPLOY_SURVEY_SUBMITTED = "Local Deploy Survey Submitted",
    DEPLOY_MODAL_OPENED = "Deploy Modal Opened",
    DEPLOY_MODAL_CLOSED = "Deploy Modal Closed",
    DEPLOY_IMAGE_MODE_CLICKED = "Deploy Image Mode Clicked",
    DEPLOY_VIDEO_MODE_CLICKED = "Deploy Video Mode Clicked",
    DEPLOY_LANGUAGE_SELECTED = "Deploy Language Selected",
    DEPLOY_BULK_UPLOAD_BANNER_SHOWN = "Deploy Bulk Upload Banner Shown",
    DEPLOY_BULK_UPLOAD_CANCELLED = "Deploy Bulk Upload Cancelled",
    DEPLOY_BULK_UPLOAD_CONTINUED = "Deploy Bulk Upload Continued",
    DEPLOY_BATCH_MODAL_OPENED = "Deploy Batch Modal Opened",
    DEPLOY_UPLOAD_MORE_DATA_CLICKED = "Deploy Upload More Data Clicked",
    DEPLOY_VIEW_JOBS_CLICKED = "Deploy View Jobs Clicked",
    DEPLOY_TERMINAL_SCRIPT_OPENED = "Deploy Terminal Script Opened",
    DEPLOY_LOCAL_CLICKED = "Deploy Local Clicked",
    BATCH_FILES_SELECTED = "Batch Files Selected",
    BATCH_RUN_MODEL_CLICKED = "Batch Run Model Clicked",
    BATCH_JOB_SUBMITTED = "Batch Job Submitted",
    BATCH_DOWNLOAD_CLICKED = "Batch Download Clicked",

    // Waitlist
    WAITLIST_JOINED = "Waitlist Joined",
    WAITLIST_LEFT = "Waitlist Left",

    // CI Credit Alerts
    CI_CREDIT_ALERT_SHOWN = "CI Credit Alert Shown",
    CI_CREDIT_ALERT_ENABLE_FLEX_CLICKED = "CI Credit Alert Enable Flex Clicked",
    CI_CREDIT_ALERT_ADD_CREDITS_CLICKED = "CI Credit Alert Add Credits Clicked",
    CI_CREDIT_ALERT_DISMISSED = "CI Credit Alert Dismissed"
}

export enum AnalyticsAuthenticationEvent {
    USER_SIGNED_UP = "User Signed Up",
    USER_SIGNED_UP_BROWSER = "User Signed Up Browser",
    USER_SIGNED_IN = "User Signed In",
    USER_SIGNED_OUT = "User Signed Out"
}

export enum AnalyticsPageView {
    HOME = "Home",
    UPLOAD = "Upload",
    PROMPT = "Prompt",
    REVIEW = "Review",
    PREVIEW = "Preview",
    DEPLOY = "Deploy"
}

export const StepToAnalyticsPageViewMap: Record<
    Exclude<Step, null | undefined>,
    AnalyticsPageView
> = {
    [BuildSteps.UPLOAD]: AnalyticsPageView.UPLOAD,
    [BuildSteps.PROMPT]: AnalyticsPageView.PROMPT,
    [BuildSteps.REVIEW]: AnalyticsPageView.REVIEW,
    [BuildSteps.PREVIEW]: AnalyticsPageView.PREVIEW,
    [DeploySteps.DEPLOY]: AnalyticsPageView.DEPLOY,
    [DeploySteps.IMPROVE]: AnalyticsPageView.REVIEW
};

export type AnalyticsEvent = AnalyticsFlowEvent | AnalyticsAuthenticationEvent;

export enum AppSource {
    RAPID = "rapid",
    RAPID_EMBEDDED = "rapid:embedded:app"
}

export type AppSourceString = AppSource | `rapid:embedded:${string}`;

export type AnalyticsEventData = Record<string, any> & {
    appSource: AppSource;
    source?: "canvas" | "thumbnail";
};

export type AnalyticsEventPayload = { type: AnalyticsEvent; data: AnalyticsEventData };

export enum AnalyticsMethod {
    TRACK = "track",
    IDENTIFY = "identify",
    PAGE = "page"
}

export type AnalyticsUserIdentity = {
    id: string;
    email: string;
    name: string;
    displayName: string;
    photo: string;
    method: string;
};

export type AnalyticsWorkspaceIdentity = {
    workspaceId: string;
    workspaceUrl: string;
    workspaceName: string;
    workspacePlan: string;
};

export type AnalyticsWorkflowIdentity = {
    workflowId: string;
    workflowUrl: string;
    workflowName: string;
};

export type AnalyticsIdentity = AnalyticsUserIdentity &
    AnalyticsWorkspaceIdentity &
    AnalyticsWorkflowIdentity;
